import React, { useState, useEffect } from 'react';
import { X, FileCheck, Loader2, Check, FileText, Contact, Briefcase, GraduationCap, CreditCard, BookOpen } from 'lucide-react';
import { adminUpdateStaffProfile, adminGetStaffProfile } from '@/services/profileService';
import type { StaffUser } from '@/services/userService';

interface PhysicalDocumentsTrackingModalProps {
  isOpen: boolean;
  onClose: () => void;
  staff: StaffUser;
  onSuccess: () => void;
}

interface PhysicalDocsState {
  physicalDocCin: boolean;
  physicalDocPassport: boolean;
  physicalDocCv: boolean;
  physicalDocContratTravail: boolean;
  physicalDocDiplome: boolean;
  physicalDocAttestationRib: boolean;
  physicalDocEngagementProfesseur: boolean;
  physicalDocReglementInterieur: boolean;
  physicalDocConsentement: boolean;
  physicalDocAutorisationEnseigner: boolean;
}

export const PhysicalDocumentsTrackingModal: React.FC<PhysicalDocumentsTrackingModalProps> = ({
  isOpen,
  onClose,
  staff,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [docs, setDocs] = useState<PhysicalDocsState>({
    physicalDocCin: false,
    physicalDocPassport: false,
    physicalDocCv: false,
    physicalDocContratTravail: false,
    physicalDocDiplome: false,
    physicalDocAttestationRib: false,
    physicalDocEngagementProfesseur: false,
    physicalDocReglementInterieur: false,
    physicalDocConsentement: false,
    physicalDocAutorisationEnseigner: false,
  });
  // Used to conditionally show CIN vs Passeport and the autorisation requirement
  const [paysOrigine, setPaysOrigine] = useState<string>('');
  const [statutProfesseur, setStatutProfesseur] = useState<string>('');
  const [fromPublicInstitution, setFromPublicInstitution] = useState<string>('');

  // Check if staff is a professor (either by role or isAlsoProfessor flag)
  const isProfessor = staff.role === 'TEACHER' || staff.isAlsoProfessor === true;
  // Foreign staff provide a passport instead of the CIN
  const isForeign = paysOrigine !== '' && paysOrigine.toUpperCase() !== 'MA';
  // Autorisation d'enseigner: only vacataire professors from a public institution
  const autorisationRequired =
    isProfessor &&
    statutProfesseur.toUpperCase() === 'V' &&
    fromPublicInstitution.toUpperCase() === 'O';

  // Fetch current document status when modal opens
  useEffect(() => {
    if (isOpen && staff) {
      fetchCurrentStatus();
    }
  }, [isOpen, staff?.id]);

  const fetchCurrentStatus = async () => {
    setFetchingData(true);
    try {
      // Fetch staff profile to get current physical document status
      const data = await adminGetStaffProfile(staff.id);

      if (data && data.profile) {
        setDocs({
          physicalDocCin: data.profile.physicalDocCin ?? false,
          physicalDocPassport: data.profile.physicalDocPassport ?? false,
          physicalDocCv: data.profile.physicalDocCv ?? false,
          physicalDocContratTravail: data.profile.physicalDocContratTravail ?? false,
          physicalDocDiplome: data.profile.physicalDocDiplome ?? false,
          physicalDocAttestationRib: data.profile.physicalDocAttestationRib ?? false,
          // Effective per-year status (the backend overlays ProfessorEngagement)
          physicalDocEngagementProfesseur: data.profile.physicalDocEngagementProfesseur ?? false,
          physicalDocReglementInterieur: data.profile.physicalDocReglementInterieur ?? false,
          physicalDocConsentement: data.profile.physicalDocConsentement ?? false,
          physicalDocAutorisationEnseigner: data.profile.physicalDocAutorisationEnseigner ?? false,
        });
        setPaysOrigine(data.profile.paysOrigine ?? '');
        setStatutProfesseur(data.profile.statutProfesseur ?? '');
        setFromPublicInstitution(data.profile.estOriginaireEtablissementPublic ?? '');
      }
    } catch (err) {
      console.error('Error fetching physical documents status:', err);
    } finally {
      setFetchingData(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      await adminUpdateStaffProfile(staff.id, {
        physicalDocCin: docs.physicalDocCin,
        physicalDocPassport: docs.physicalDocPassport,
        physicalDocCv: docs.physicalDocCv,
        physicalDocContratTravail: docs.physicalDocContratTravail,
        physicalDocDiplome: docs.physicalDocDiplome,
        physicalDocAttestationRib: docs.physicalDocAttestationRib,
        physicalDocEngagementProfesseur: docs.physicalDocEngagementProfesseur,
        physicalDocReglementInterieur: docs.physicalDocReglementInterieur,
        physicalDocConsentement: docs.physicalDocConsentement,
        physicalDocAutorisationEnseigner: docs.physicalDocAutorisationEnseigner,
      });

      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  const toggleDoc = (key: keyof PhysicalDocsState) => {
    setDocs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Count received documents. CIN and Passeport are ONE requirement (either
  // satisfies it) — a person is never expected to provide both.
  const receivedCount =
    (docs.physicalDocCin || docs.physicalDocPassport ? 1 : 0) +
    (docs.physicalDocCv ? 1 : 0) +
    (docs.physicalDocContratTravail ? 1 : 0) +
    (docs.physicalDocDiplome ? 1 : 0) +
    (docs.physicalDocAttestationRib ? 1 : 0) +
    (docs.physicalDocReglementInterieur ? 1 : 0) +
    (docs.physicalDocConsentement ? 1 : 0) +
    (isProfessor && docs.physicalDocEngagementProfesseur ? 1 : 0) +
    (autorisationRequired && docs.physicalDocAutorisationEnseigner ? 1 : 0);

  // Base 7 (CIN ou Passeport, CV, Contrat, Diplome, RIB, Reglement, Consentement)
  // + Engagement for professors + Autorisation for vacataires from public institutions
  const totalDocs = 7 + (isProfessor ? 1 : 0) + (autorisationRequired ? 1 : 0);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <FileCheck className="text-white" size={20} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">
                  Suivi des documents physiques
                </h2>
                <p className="text-sm text-emerald-100">
                  {staff.firstName} {staff.lastName}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {fetchingData ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="animate-spin text-emerald-600" size={32} />
              </div>
            ) : (
              <>
                {/* Progress indicator */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Documents physiques</span>
                    <span className="text-sm font-semibold text-emerald-600">
                      {receivedCount} / {totalDocs}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(receivedCount / totalDocs) * 100}%` }}
                    />
                  </div>
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {error}
                  </div>
                )}

                {/* Document checkboxes */}
                <div className="space-y-3">
                  {/* CIN (Moroccans) OR Passeport (foreigners) — one identity requirement */}
                  {!isForeign ? (
                    <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={docs.physicalDocCin}
                        onChange={() => toggleDoc('physicalDocCin')}
                        className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                      />
                      <Contact className="text-gray-500" size={20} />
                      <div className="flex-1">
                        <span className="font-medium text-gray-900">CIN</span>
                        <p className="text-xs text-gray-500">Carte d'identite nationale</p>
                      </div>
                      {docs.physicalDocCin && <Check className="text-emerald-600" size={18} />}
                    </label>
                  ) : (
                    <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={docs.physicalDocPassport}
                        onChange={() => toggleDoc('physicalDocPassport')}
                        className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                      />
                      <FileText className="text-gray-500" size={20} />
                      <div className="flex-1">
                        <span className="font-medium text-gray-900">Passeport</span>
                        <p className="text-xs text-gray-500">Copie du passeport (personnel etranger)</p>
                      </div>
                      {docs.physicalDocPassport && <Check className="text-emerald-600" size={18} />}
                    </label>
                  )}

                  {/* CV */}
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={docs.physicalDocCv}
                      onChange={() => toggleDoc('physicalDocCv')}
                      className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                    />
                    <FileText className="text-gray-500" size={20} />
                    <div className="flex-1">
                      <span className="font-medium text-gray-900">CV</span>
                      <p className="text-xs text-gray-500">Curriculum Vitae</p>
                    </div>
                    {docs.physicalDocCv && <Check className="text-emerald-600" size={18} />}
                  </label>

                  {/* Contrat de travail */}
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={docs.physicalDocContratTravail}
                      onChange={() => toggleDoc('physicalDocContratTravail')}
                      className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                    />
                    <Briefcase className="text-gray-500" size={20} />
                    <div className="flex-1">
                      <span className="font-medium text-gray-900">Contrat de travail</span>
                      <p className="text-xs text-gray-500">Contrat de travail signe</p>
                    </div>
                    {docs.physicalDocContratTravail && <Check className="text-emerald-600" size={18} />}
                  </label>

                  {/* Diplome */}
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={docs.physicalDocDiplome}
                      onChange={() => toggleDoc('physicalDocDiplome')}
                      className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                    />
                    <GraduationCap className="text-gray-500" size={20} />
                    <div className="flex-1">
                      <span className="font-medium text-gray-900">Diplome</span>
                      <p className="text-xs text-gray-500">Copie du diplome le plus eleve</p>
                    </div>
                    {docs.physicalDocDiplome && <Check className="text-emerald-600" size={18} />}
                  </label>

                  {/* Attestation RIB */}
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={docs.physicalDocAttestationRib}
                      onChange={() => toggleDoc('physicalDocAttestationRib')}
                      className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                    />
                    <CreditCard className="text-gray-500" size={20} />
                    <div className="flex-1">
                      <span className="font-medium text-gray-900">Attestation RIB</span>
                      <p className="text-xs text-gray-500">Releve d'identite bancaire</p>
                    </div>
                    {docs.physicalDocAttestationRib && <Check className="text-emerald-600" size={18} />}
                  </label>

                  {/* Reglement interieur */}
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={docs.physicalDocReglementInterieur}
                      onChange={() => toggleDoc('physicalDocReglementInterieur')}
                      className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                    />
                    <FileText className="text-gray-500" size={20} />
                    <div className="flex-1">
                      <span className="font-medium text-gray-900">Reglement interieur</span>
                      <p className="text-xs text-gray-500">Reglement interieur signe</p>
                    </div>
                    {docs.physicalDocReglementInterieur && <Check className="text-emerald-600" size={18} />}
                  </label>

                  {/* Consentement medias */}
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={docs.physicalDocConsentement}
                      onChange={() => toggleDoc('physicalDocConsentement')}
                      className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                    />
                    <FileText className="text-gray-500" size={20} />
                    <div className="flex-1">
                      <span className="font-medium text-gray-900">Consentement medias</span>
                      <p className="text-xs text-gray-500">Consentement medias et reseaux sociaux</p>
                    </div>
                    {docs.physicalDocConsentement && <Check className="text-emerald-600" size={18} />}
                  </label>

                  {/* Engagement des professeurs - only for professors */}
                  {isProfessor && (
                    <label className="flex items-center gap-3 p-3 border border-amber-200 bg-amber-50 rounded-lg hover:bg-amber-100 cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={docs.physicalDocEngagementProfesseur}
                        onChange={() => toggleDoc('physicalDocEngagementProfesseur')}
                        className="w-5 h-5 text-amber-600 border-amber-300 rounded focus:ring-amber-500"
                      />
                      <BookOpen className="text-amber-600" size={20} />
                      <div className="flex-1">
                        <span className="font-medium text-amber-900">Engagement des professeurs</span>
                        <p className="text-xs text-amber-700">Document d'engagement pour les activites d'enseignement</p>
                      </div>
                      {docs.physicalDocEngagementProfesseur && <Check className="text-amber-600" size={18} />}
                    </label>
                  )}

                  {/* Autorisation d'enseigner - only for vacataires from a public institution */}
                  {autorisationRequired && (
                    <label className="flex items-center gap-3 p-3 border border-amber-200 bg-amber-50 rounded-lg hover:bg-amber-100 cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={docs.physicalDocAutorisationEnseigner}
                        onChange={() => toggleDoc('physicalDocAutorisationEnseigner')}
                        className="w-5 h-5 text-amber-600 border-amber-300 rounded focus:ring-amber-500"
                      />
                      <BookOpen className="text-amber-600" size={20} />
                      <div className="flex-1">
                        <span className="font-medium text-amber-900">Autorisation d'enseigner</span>
                        <p className="text-xs text-amber-700">Vacataire issu d'un etablissement public - copie physique de l'autorisation</p>
                      </div>
                      {docs.physicalDocAutorisationEnseigner && <Check className="text-amber-600" size={18} />}
                    </label>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={loading || success || fetchingData}
              className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  <span>Enregistrement...</span>
                </>
              ) : success ? (
                <>
                  <Check size={18} />
                  <span>Enregistre !</span>
                </>
              ) : (
                <span>Enregistrer</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhysicalDocumentsTrackingModal;
